/**
 * TasksPage - the agent's own prioritized Tasks list (plan
 * plans/2026-08-08-reminders-and-tasks.md section 5, reworked
 * same-day per Greg's first review). Route /tasks, agent-only via the
 * same AgentOnlyRoute guard as /activity.
 *
 * Layout is a columned table like the Activity feed (Greg: "Tasks
 * should be more like Activity"), sharing the tickets-list-table
 * mobile card treatment plus tasks-specific rules in styles/index.css.
 * Clicking a row expands it in place; the expansion holds the
 * description AND the Edit / Delete actions (moved off the row per
 * Greg - the row itself carries only the outline Dismiss button).
 *
 * Open view renders the server's ordinal order and never re-sorts;
 * drag-to-reorder uses native HTML5 drag events (no new dependency)
 * and posts the FULL ordered id list to /agent/tasks/reorder. A 409
 * means the drag raced a create / dismiss (often the outside AI's
 * pass) - the page toasts and refetches rather than guessing.
 *
 * Dismiss = done, kept forever; Delete is the hard remove for tasks
 * that should never have existed and gets a confirm modal spelling
 * out that difference.
 */
import { Fragment, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { Button, Modal, Spinner } from "@/components/ui";
import { TaskCategoryBadge } from "@/components/tasks/TaskCategoryBadge";
import { TaskFormModal } from "@/components/tasks/TaskFormModal";
import { TASKS_QUERY_KEY, useTaskCount, useTasks } from "@/hooks/useTasks";
import {
  useDeleteTask,
  useDismissTask,
  useReorderTasks,
} from "@/hooks/useTaskMutations";
import { usePageTitle } from "@/hooks/usePageTitle";
import { safeHtmlProps } from "@/lib/sanitize";
import { formatDueDate, isOverdue } from "@/lib/taskDates";
import type { TaskResponse, TaskStatus } from "@/types/api";

/** Absolute done stamp for the Done view ("Aug 8, 2:30 PM"). Done is
 * a ledger, not a feed, so an absolute time reads better than the
 * relative wording the Activity feed uses. */
function formatDoneAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Bottom-center transient notice. Same visual family as UndoToast
 * but with no action button - deliberately local to this page rather
 * than a new design-system primitive (ui/* mirrors Auware). */
function TasksToast({ message }: { message: string }): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed bottom-6 left-1/2 z-[10000] -translate-x-1/2"
    >
      <div className="rounded-lg bg-neutral-900 px-4 py-3 text-sm text-white shadow-lg ring-1 ring-black/10">
        {message}
      </div>
    </div>
  );
}

/** The ticket chip on a task row. Stops row-level handlers so the
 * click only navigates (the row click toggles expansion). */
function TicketChip({ code }: { code: string }): JSX.Element {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/tickets/${code.toLowerCase()}`);
      }}
      className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 font-mono text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
      data-testid="task-ticket-chip"
    >
      <i className="fa-duotone fa-thin fa-ticket" aria-hidden="true" />
      {code}
    </button>
  );
}

export function TasksPage(): JSX.Element {
  usePageTitle("Tasks - Buyist Support");
  const [view, setView] = useState<TaskStatus>("open");
  const list = useTasks(view);
  const count = useTaskCount();
  const queryClient = useQueryClient();

  const dismiss = useDismissTask();
  const remove = useDeleteTask();
  const reorder = useReorderTasks();

  const [formOpen, setFormOpen] = useState(false);
  const [editTask, setEditTask] = useState<TaskResponse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TaskResponse | null>(null);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  function flash(message: string) {
    setToast(message);
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 5000);
  }

  const serverRows = useMemo(() => list.data ?? [], [list.data]);

  // Drag state: the id being dragged and the preview order shown while
  // the drag is in flight. Preview is ids-only; rows resolve through
  // the server data so nothing is cloned.
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [previewIds, setPreviewIds] = useState<string[] | null>(null);

  const displayRows = useMemo(() => {
    if (view !== "open" || !previewIds) return serverRows;
    const byId = new Map(serverRows.map((t) => [t.id, t]));
    return previewIds
      .map((id) => byId.get(id))
      .filter((t): t is TaskResponse => !!t);
  }, [view, previewIds, serverRows]);

  function handleDragStart(e: React.DragEvent, id: string) {
    setDraggingId(id);
    setPreviewIds(serverRows.map((t) => t.id));
    // dataTransfer is absent in jsdom; optional-chain keeps the
    // handler test-runnable.
    e.dataTransfer?.setData("text/plain", id);
    if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
  }

  function handleDragOver(e: React.DragEvent, overId: string) {
    e.preventDefault();
    if (!draggingId || draggingId === overId) return;
    setPreviewIds((ids) => {
      if (!ids) return ids;
      const without = ids.filter((id) => id !== draggingId);
      const at = without.indexOf(overId);
      if (at === -1) return ids;
      const next = [...without];
      next.splice(at, 0, draggingId);
      return next;
    });
  }

  function commitDrag() {
    const ordered = previewIds;
    setDraggingId(null);
    setPreviewIds(null);
    if (!ordered) return;
    const serverIds = serverRows.map((t) => t.id);
    if (ordered.join(",") === serverIds.join(",")) return;
    reorder.mutate(ordered, {
      onError: (err) => {
        if (isAxiosError(err) && err.response?.status === 409) {
          flash("The list changed - reloading.");
        } else {
          flash("Reorder failed.");
        }
        // The hook invalidates on settled either way; this refetch is
        // belt-and-braces so the list snaps back immediately.
        void queryClient.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
      },
    });
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    commitDrag();
  }

  function handleDragEnd() {
    // Fires after drop on a normal drag, or alone on a cancelled one.
    // commitDrag is a no-op when drop already committed (state
    // cleared) or when the order did not change.
    commitDrag();
  }

  function toggleExpanded(id: string) {
    setExpandedIds((m) => ({ ...m, [id]: !m[id] }));
  }

  const openCount = count.data?.open;
  const rows = displayRows;
  const isOpenView = view === "open";
  // Open view: handle + ordinal + category + task + ticket + due +
  // actions. Done view: category + task + ticket + done.
  const columnCount = isOpenView ? 7 : 4;

  return (
    <div className="w-full">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <i
            className="fa-duotone fa-thin fa-list-check text-3xl text-primary-600"
            aria-hidden="true"
          />
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">Tasks</h1>
            <p
              className="mt-1 text-sm text-neutral-500"
              data-testid="tasks-subtitle"
            >
              {openCount === undefined
                ? "Loading..."
                : `${openCount} open task${openCount === 1 ? "" : "s"}`}
            </p>
          </div>
        </div>
        <Button
          type="button"
          onClick={() => {
            setEditTask(null);
            setFormOpen(true);
          }}
          data-testid="new-task-button"
        >
          <i className="fa-duotone fa-thin fa-plus" aria-hidden="true" />
          New Task
        </Button>
      </div>

      {/* Open | Done view toggle. */}
      <div
        className="mb-4 inline-flex rounded-lg border border-neutral-200 bg-white p-1"
        role="tablist"
        aria-label="Task views"
      >
        {(["open", "done"] as const).map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            onClick={() => setView(v)}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              view === v
                ? "bg-primary-50 text-primary-700"
                : "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
            }`}
            data-testid={`tasks-view-${v}`}
          >
            {v === "open" ? "Open" : "Done"}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white max-lg:border-0 max-lg:bg-transparent">
        {list.isLoading && (
          <div className="flex items-center justify-center px-4 py-12">
            <Spinner />
          </div>
        )}

        {list.isError && (
          <div className="px-4 py-8 text-center text-sm text-red-700">
            {list.error instanceof Error
              ? list.error.message
              : "Failed to load tasks."}
          </div>
        )}

        {!list.isLoading && !list.isError && rows.length === 0 && (
          <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
            <i
              className="fa-duotone fa-thin fa-list-check text-4xl text-neutral-300"
              aria-hidden="true"
            />
            <p className="mt-2 text-sm font-medium text-neutral-700">
              {view === "open" ? "No open tasks." : "No completed tasks yet."}
            </p>
          </div>
        )}

        {!list.isLoading && !list.isError && rows.length > 0 && (
          <table className="tickets-list-table tasks-list-table min-w-full divide-y divide-neutral-100">
            <thead className="bg-neutral-50">
              <tr className="text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                {isOpenView && (
                  <>
                    {/* Drag handle column - no header label. */}
                    <th className="w-8 px-3 py-2" aria-label="Reorder" />
                    <th className="w-8 px-3 py-2">#</th>
                  </>
                )}
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Task</th>
                <th className="px-3 py-2">Ticket</th>
                {isOpenView ? (
                  <>
                    <th className="whitespace-nowrap px-3 py-2">Due</th>
                    <th className="px-3 py-2" aria-label="Actions" />
                  </>
                ) : (
                  <th className="whitespace-nowrap px-3 py-2">Done</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {rows.map((task, idx) => {
                const hasDescription =
                  task.description_text.trim().length > 0 ||
                  !!task.description_html;
                const expanded = !!expandedIds[task.id];
                // Open rows always expand (the expansion holds Edit /
                // Delete); done rows only when there is a description
                // to show.
                const expandable = isOpenView || hasDescription;
                const overdue =
                  isOpenView &&
                  task.status === "open" &&
                  isOverdue(task.due_date);
                // A keyed Fragment, NOT a plain array: an array child
                // only gets an index-based implicit key, so a preview
                // reorder would remount every row (killing the very
                // DOM nodes a drag is interacting with) instead of
                // moving them.
                return (
                  <Fragment key={task.id}>
                  <tr
                    draggable={isOpenView}
                    onDragStart={
                      isOpenView
                        ? (e) => handleDragStart(e, task.id)
                        : undefined
                    }
                    onDragOver={
                      isOpenView
                        ? (e) => handleDragOver(e, task.id)
                        : undefined
                    }
                    onDrop={isOpenView ? handleDrop : undefined}
                    onDragEnd={isOpenView ? handleDragEnd : undefined}
                    onClick={
                      expandable ? () => toggleExpanded(task.id) : undefined
                    }
                    className={`${expandable ? "cursor-pointer " : ""}transition-colors hover:bg-neutral-50 ${
                      draggingId === task.id ? "bg-primary-50/60" : ""
                    }`}
                    data-testid="task-row"
                    data-task-id={task.id}
                  >
                    {isOpenView && (
                      <>
                        <td
                          className="tickets-card-code w-8 px-3 py-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span
                            className="cursor-grab text-neutral-300 hover:text-neutral-500"
                            title="Drag to reorder"
                            data-testid="task-drag-handle"
                          >
                            <i
                              className="fa-duotone fa-thin fa-grip-dots-vertical text-lg"
                              aria-hidden="true"
                            />
                          </span>
                        </td>
                        <td
                          className="tickets-card-code w-8 px-3 py-3 text-right font-mono text-xs text-neutral-400"
                          data-testid="task-ordinal"
                        >
                          {idx + 1}
                        </td>
                      </>
                    )}
                    <td
                      className="whitespace-nowrap px-3 py-3"
                      data-label="Category"
                    >
                      <TaskCategoryBadge category={task.category} />
                    </td>
                    <td
                      className="tickets-card-subject px-3 py-3 text-sm font-medium text-neutral-900"
                      data-label="Task"
                      data-code={isOpenView ? `#${idx + 1}` : "Done"}
                    >
                      <span data-testid="task-title">{task.title}</span>
                      {task.attachment_count > 0 && (
                        <span
                          className="ml-2 text-xs font-normal text-neutral-500"
                          data-testid="task-attachment-count"
                        >
                          <i
                            className="fa-duotone fa-thin fa-paperclip mr-1"
                            aria-hidden="true"
                          />
                          {task.attachment_count}
                        </span>
                      )}
                      {expandable && (
                        <i
                          className={`fa-duotone fa-thin ${
                            expanded ? "fa-chevron-up" : "fa-chevron-down"
                          } ml-2 text-xs text-neutral-400`}
                          aria-hidden="true"
                        />
                      )}
                    </td>
                    <td
                      className="whitespace-nowrap px-3 py-3"
                      data-label="Linked ticket"
                    >
                      {task.ticket_public_code ? (
                        <TicketChip code={task.ticket_public_code} />
                      ) : null}
                    </td>
                    {isOpenView ? (
                      <>
                        <td
                          className={`whitespace-nowrap px-3 py-3 text-xs ${
                            overdue
                              ? "font-medium text-red-600"
                              : "text-neutral-500"
                          }`}
                          data-label="Due"
                          data-testid={task.due_date ? "task-due-date" : undefined}
                          data-overdue={overdue ? "true" : "false"}
                        >
                          {task.due_date ? formatDueDate(task.due_date) : ""}
                        </td>
                        <td
                          className="whitespace-nowrap px-3 py-3 text-right"
                          data-label="Actions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Outline, not primary (Greg: the solid
                              button was too heavy - match the ticket
                              header's Delete/Close buttons). */}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              dismiss.mutate(task.id, {
                                onError: () => flash("Dismiss failed."),
                              })
                            }
                            data-testid="task-dismiss"
                          >
                            <i
                              className="fa-duotone fa-thin fa-check"
                              aria-hidden="true"
                            />
                            Dismiss
                          </Button>
                        </td>
                      </>
                    ) : (
                      <td
                        className="whitespace-nowrap px-3 py-3 text-xs text-neutral-500"
                        data-label="Done"
                      >
                        {task.done_at ? formatDoneAt(task.done_at) : ""}
                        {" - "}
                        {task.owner_name || task.owner_email}
                      </td>
                    )}
                  </tr>
                  {expanded && (
                    <tr
                      className="bg-neutral-50/60"
                      data-testid="task-expansion"
                    >
                      <td colSpan={columnCount} className="px-4 py-3">
                        {hasDescription ? (
                          <div className="text-sm text-neutral-700">
                            {task.description_html ? (
                              <div
                                className="prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={safeHtmlProps(
                                  task.description_html,
                                  { collapseQuotes: false },
                                )}
                              />
                            ) : (
                              <p className="whitespace-pre-wrap">
                                {task.description_text}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm italic text-neutral-400">
                            No description.
                          </p>
                        )}
                        {isOpenView && (
                          <div className="mt-3 flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditTask(task);
                                setFormOpen(true);
                              }}
                              data-testid="task-edit"
                            >
                              <i
                                className="fa-duotone fa-thin fa-pen"
                                aria-hidden="true"
                              />
                              Edit
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="border-red-300 text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteTarget(task)}
                              data-testid="task-delete"
                            >
                              <i
                                className="fa-duotone fa-thin fa-trash-can"
                                aria-hidden="true"
                              />
                              Delete
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <TaskFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditTask(null);
        }}
        task={editTask}
      />

      <Modal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Delete Task"
        size="sm"
      >
        <p className="text-sm text-neutral-700">
          Delete <span className="font-medium">{deleteTarget?.title}</span>{" "}
          permanently? Unlike Dismiss, a deleted task leaves no trace: it
          will not appear in the Done view or the Activity feed, and its
          attachments are removed for good. Use Dismiss for work that is
          finished.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setDeleteTarget(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50"
            isLoading={remove.isPending}
            onClick={() => {
              if (!deleteTarget) return;
              remove.mutate(deleteTarget.id, {
                onSuccess: () => setDeleteTarget(null),
                onError: () => flash("Delete failed."),
              });
            }}
            data-testid="task-delete-confirm"
          >
            Delete permanently
          </Button>
        </div>
      </Modal>

      {toast && <TasksToast message={toast} />}
    </div>
  );
}
